import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import SEO from '../components/SEO';

const ContactPage = () => {
  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Alpha Consulting LLC',
    url: 'https://thealphaconsult.com/contact',
    about: {
      '@type': 'ProfessionalService',
      name: 'Alpha Consulting LLC',
      telephone: '+1-770-545-1557',
      email: 'info@thealphaconsult.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Atlanta',
        addressRegion: 'GA',
        addressCountry: 'US'
      }
    }
  };

  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    budget: '',
    plan: '',
    product: '',
    timeline: '',
    message: ''
  });
  const [captchaToken, setCaptchaToken] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('Sending...');
    if ((process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').length > 0 && !captchaToken) {
      setFormStatus('Please complete the reCAPTCHA.');
      return;
    }
    
    try {
      // Use environment variable for API URL (falls back to localhost for dev)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Send data to backend API
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, recaptchaToken: captchaToken }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus('Thanks! We will reach out shortly.');
        
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          company: '',
          budget: '',
          plan: '',
          product: '',
          timeline: '',
          message: ''
        });
        setCaptchaToken('');
      } else {
        setFormStatus(`Error: ${data.message || 'Failed to submit form'}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('Error: Could not connect to server. Please try again.');
    }
  };

  return (
    <div className="pt-24">
      <SEO 
        title="Contact Our Atlanta Technology Consulting Team | Alpha Consulting LLC"
        description="Contact our Atlanta, Georgia technology consulting team for custom software development, cloud projects, system modernization, automation, and product engineering support."
        keywords="contact Atlanta technology consulting company, Georgia software development consultation, request proposal Atlanta, custom software company contact Georgia"
        url="https://thealphaconsult.com/contact"
        image="https://thealphaconsult.com/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png"
        structuredData={contactSchema}
      />
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300/80 font-nunito">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> Request a proposal
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-nunito font-bold">
                Tell us about your software or cloud project
              </h2>
              <p className="mt-3 text-orange-300 font-nunito">
                We work with companies in Atlanta, across Georgia, and throughout the USA. Share your goals and we&apos;ll respond within 1 business day with a tailored next step.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-900/60 border border-white/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 5h18M8 5v14m8-14v14M3 19h18"></path>
                    </svg>
                  </div>
                  <p className="text-orange-200 font-nunito">Clear scope, timeline, and pricing</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-900/60 border border-white/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="m9 12 2 2 4-4"></path>
                      <path d="M12 22a10 10 0 1 0-10-10"></path>
                    </svg>
                  </div>
                  <p className="text-orange-200 font-nunito">NDA available upon request</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-900/60 border border-white/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 2v20M2 12h20"></path>
                    </svg>
                  </div>
                  <p className="text-orange-200 font-nunito">Flexible engagement models</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-900/60 border border-white/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 21a9 9 0 1 0-9-9"></path>
                      <path d="M3.6 9h16.8"></path>
                      <path d="M12 3a15 15 0 0 1 4 9 15 15 0 0 1-4 9 15 15 0 0 1-4-9 15 15 0 0 1 4-9Z"></path>
                    </svg>
                  </div>
                  <p className="text-orange-200 font-nunito">Serving Atlanta, Georgia, and remote teams nationwide</p>
                </div>
              </div>

              <div className="mt-10 rounded-2xl border border-white/10 bg-orange-900/40 p-6">
                <h3 className="text-lg font-semibold font-nunito mb-4">Contact Information</h3>
                <div className="space-y-3 text-orange-300 font-nunito">
                  <p className="flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                    </svg>
                    info@thealphaconsult.com
                  </p>
                  <p className="flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    +1 (770) 545-1557
                  </p>
                  <p className="flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    Atlanta, Georgia, USA.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm text-orange-200 font-nunito mb-1">
                      Your name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white"
                      placeholder="Alex Johnson"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-orange-200 font-nunito mb-1">
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white"
                      placeholder="alex@company.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="company" className="block text-sm text-orange-200 font-nunito mb-1">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white"
                      placeholder="Acme Inc."
                    />
                  </div>
                  <div>
                    <label htmlFor="budget" className="block text-sm text-orange-200 font-nunito mb-1">
                      Approx. budget
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Select a range</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>$6k – $25k</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>$25k – $50k</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>$50k – $100k</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>$100k+</option>
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="plan" className="block text-sm text-orange-200 font-nunito mb-1">
                      Plan Interest
                    </label>
                    <select
                      id="plan"
                      name="plan"
                      value={formData.plan}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Select a Plan</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Starter Sprint</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Growth</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Dedicated Team</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="product" className="block text-sm text-orange-200 font-nunito mb-1">
                      Product Interest
                    </label>
                    <select
                      id="product"
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Select a Product</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Hardware Reconciliation</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>custom software</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>mobile app</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>web app</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>e-commerce</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>CRM</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>automation</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Security</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>analytics</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>ERP</option>
                      <option style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>Scheduling Automation</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm text-orange-200 font-nunito mb-1">
                    Timeline
                  </label>
                  <input
                    id="timeline"
                    name="timeline"
                    type="text"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white"
                    placeholder="e.g., start next month, 12 weeks"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm text-orange-200 font-nunito mb-1">
                    Project details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-white resize-none"
                    placeholder="Short description of goals, users, and success metrics..."
                  />
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-orange-300 font-nunito" role="status" aria-live="polite">
                    {formStatus}
                  </div>
                  <div className="ml-auto mr-3">
                    {process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setCaptchaToken(token || '')}
                      />
                    ) : null}
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-orange-100 text-black hover:bg-orange-200 transition font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 font-nunito"
                  >
                    Send request
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M5 12h14M12 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
